import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collateralApi, ApiError } from '../services/collateralApi';
import type { VehicleListItem } from '../types/Vehicle';

export default function useVehiclePageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [vehicleType, setVehicleType] = useState('');
    const [archived, setArchived] = useState('false');
    const [rows, setRows] = useState<VehicleListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 100;
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filterCounts, setFilterCounts] = useState<Record<string, any>>({});
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    // Side panel
    const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
    const [vehicleDetail, setVehicleDetail] = useState<any>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getVehicles({
                page,
                pageSize,
                search: search || undefined,
                vehicleType: vehicleType || undefined,
                archived: archived || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setFilterCounts(result.filterCounts || {});
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, search, vehicleType, archived, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const handleRowClick = async (row: VehicleListItem) => {
        setSelectedVehicle(row);
        setIsLoadingDetail(true);
        try {
            const detail = await collateralApi.getVehicleView(row.id);
            setVehicleDetail(detail);
        } catch {
            setVehicleDetail(null);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const closePanel = () => {
        setSelectedVehicle(null);
        setVehicleDetail(null);
    };

    return {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading, filterCounts,
        showNewForm, setShowNewForm,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        vehicleType, setVehicleType,
        archived, setArchived,
        refresh, handleRowClick,
        selectedVehicle, vehicleDetail, isLoadingDetail, closePanel,
        apiInfo,
    };
}
